import React, { useState, useEffect } from 'react';
import { CATEGORIES, API_BASE_URL } from '../data/artworkData';

const AdminPage = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [artworks, setArtworks] = useState([]);

    // Form state
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState(CATEGORIES[0]);
    const [description, setDescription] = useState('');
    const [location, setLocation] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    const hardcodedPassword = 'admin'; // For demonstration

    useEffect(() => {
        if (isAuthenticated) {
            fetchUploadedArtworks();
        }
    }, [isAuthenticated]);

    const fetchUploadedArtworks = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/artworks`);
            const data = await response.json();
            setArtworks(data);
        } catch (error) {
            console.error('Error fetching artworks:', error);
        }
    };

    const handleLogin = (e) => {
        e.preventDefault();
        if (password === hardcodedPassword) {
            setIsAuthenticated(true);
            setMessage({ text: 'Logged in successfully', type: 'success' });
        } else {
            setMessage({ text: 'Incorrect password', type: 'error' });
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!imageFile) {
            setMessage({ text: 'Please select an image', type: 'error' });
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('title', title);
        formData.append('category', category);
        formData.append('description', description);
        formData.append('location', location);
        formData.append('image', imageFile);

        try {
            const response = await fetch(`${API_BASE_URL}/api/artworks`, {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                setMessage({ text: 'Artwork uploaded successfully!', type: 'success' });
                setTitle('');
                setDescription('');
                setLocation('');
                setImageFile(null);
                // Reset file input
                document.getElementById('image-input').value = '';
                fetchUploadedArtworks();
            } else {
                const errorData = await response.json().catch(() => ({}));
                const errorMsg = errorData.message || 'Upload failed';
                console.error('Upload error:', errorMsg, errorData);
                setMessage({ text: errorMsg, type: 'error' });
            }
        } catch (error) {
            setMessage({ text: 'Error uploading image', type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this artwork?')) return;

        try {
            // Use POST with _method=DELETE since some hosts (e.g. Hostinger) block DELETE method
            const formData = new FormData();
            formData.append('_method', 'DELETE');

            const response = await fetch(`${API_BASE_URL}/api/artworks/${id}`, {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                setMessage({ text: 'Artwork deleted', type: 'success' });
                fetchUploadedArtworks();
            }
        } catch (error) {
            setMessage({ text: 'Error deleting artwork', type: 'error' });
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 pt-20">
                <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md">
                    <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">Admin Login</h2>
                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Passphrase</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                                placeholder="Enter password"
                            />
                        </div>
                        <button
                            type="submit"
                            className="w-full py-2 bg-primary-600 text-white font-bold rounded-lg hover:bg-primary-700 transition-colors"
                        >
                            Enter Dashboard
                        </button>
                        {message.text && (
                            <p className={`text-center text-sm ${message.type === 'error' ? 'text-red-500' : 'text-green-500'}`}>
                                {message.text}
                            </p>
                        )}
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pt-32 pb-20 px-4">
            <div className="max-w-6xl mx-auto">
                <div className="flex justify-between items-center mb-10">
                    <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard - Manage Artworks</h1>
                    <button
                        onClick={() => setIsAuthenticated(false)}
                        className="text-gray-500 hover:text-gray-800 font-medium"
                    >
                        Logout
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                    {/* Upload Form */}
                    <div className="lg:col-span-1">
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 sticky top-32">
                            <h2 className="text-xl font-bold mb-6 flex items-center">
                                <span className="mr-2">📤</span> Upload New Piece
                            </h2>
                            <form onSubmit={handleUpload} className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Title</label>
                                    <input
                                        type="text"
                                        required
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500 outline-none text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Category</label>
                                    <select
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500 outline-none text-sm"
                                    >
                                        {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Description (Optional)</label>
                                    <textarea
                                        rows="3"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500 outline-none text-sm"
                                    ></textarea>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Location (Optional)</label>
                                    <input
                                        type="text"
                                        value={location}
                                        onChange={(e) => setLocation(e.target.value)}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500 outline-none text-sm"
                                        placeholder="e.g. New Delhi, India"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Image File</label>
                                    <input
                                        id="image-input"
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setImageFile(e.target.files[0])}
                                        className="text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 transition-all"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={uploading}
                                    className={`w-full py-3 bg-primary-600 text-white font-bold rounded-xl hover:bg-primary-700 transition-all transform active:scale-95 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {uploading ? 'Processing...' : 'Add to Gallery'}
                                </button>
                                {message.text && (
                                    <p className={`text-center text-sm font-medium mt-2 ${message.type === 'error' ? 'text-red-500' : 'text-green-500'}`}>
                                        {message.text}
                                    </p>
                                )}
                            </form>
                        </div>
                    </div>

                    {/* Uploaded List */}
                    <div className="lg:col-span-2">
                        <h2 className="text-xl font-bold mb-6 flex items-center">
                            <span className="mr-2">🖼️</span> Uploaded Artworks ({artworks.length})
                        </h2>

                        {artworks.length === 0 ? (
                            <div className="bg-white p-10 rounded-2xl text-center border-2 border-dashed border-gray-200">
                                <p className="text-gray-400 font-medium">No custom uploads yet. Use the form to add some!</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {artworks.map((art) => (
                                    <div key={art.id} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 group">
                                        <div className="aspect-video relative overflow-hidden">
                                            <img
                                                src={`${API_BASE_URL}${art.image}`}
                                                alt={art.title}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute top-2 right-2 flex gap-2">
                                                <button
                                                    onClick={() => handleDelete(art.id)}
                                                    className="p-2 bg-red-500/80 hover:bg-red-600 text-white rounded-full backdrop-blur-sm transition-all shadow-lg"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                            <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                                                <p className="text-white text-xs font-bold uppercase tracking-wider">{art.category}</p>
                                            </div>
                                        </div>
                                        <div className="p-4">
                                            <h3 className="font-bold text-gray-900 mb-1">{art.title}</h3>
                                            {art.location && <p className="text-xs text-gray-500 flex items-center"><span className="mr-1">📍</span> {art.location}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminPage;
